import { onBeforeUnmount } from 'vue'

import type { WsBookingEvent } from '@brighterly/lib-core-types/ws/interfaces'
import type { Booking } from '@brighterly/lib-core-types/booking/interfaces'

import { WsEvent } from '@shared/api'
import { useEcho } from '@shared/api'
import { flushBeforeLeave } from '@shared/lib/amplitude'

import { usePingLessonApi } from '../../api/ping-lesson.hook'

interface LessonRoomWsOptions {
  bookingId: number
  disciplineId: number
  onUpdate?: (booking: Booking) => void
}

interface LessonRoomWsReturn {
  listen: () => void
}

export const useLessonRoomWs = ({ bookingId, disciplineId, onUpdate }: LessonRoomWsOptions): LessonRoomWsReturn => {
  const $echo = useEcho()
  const { pingLesson } = usePingLessonApi()

  const channelName = `cs.discipline.${disciplineId}`

  const onBookingUpdate = async (event: WsBookingEvent): Promise<void> => {
    if (event.id !== bookingId) return

    const booking = await pingLesson(bookingId)
    if (!booking) return

    onUpdate?.(booking)
  }

  // Admin (AttendanceSetter) force-refreshes the kid's lesson page.
  const onLessonRefreshed = (): void => {
    flushBeforeLeave()
    window.location.reload()
  }

  const listen = (): void => {
    $echo.private(channelName)
      .listen(WsEvent.BookingUpdated, onBookingUpdate)
      .listen(WsEvent.LessonRefreshed, onLessonRefreshed)
  }

  onBeforeUnmount(() => {
    $echo.leave(channelName)
  })

  return { listen }
}
